import { useCallback, useEffect, useRef, useState } from "react";
import { useGomakeAxios } from "@/hooks/use-gomake-axios";
import { useSnackBar } from "@/hooks/use-snack-bar";
import { DOCUMENT_TYPE } from "@/pages-components/quotes/enums";
import {
  assignToTicketApi,
  getDocumentMailDataApi,
  sendDocumentToClientApi,
} from "@/services/api-service/generic-doc/documents-api";
import {
  getFrontDeskDocumentSendService,
  type DocumentSendChannel,
  type DocumentSendOptions,
} from "@/services/frontdesk/api/frontdesk-document-send.service";
import type { ComposerPrefillRequest } from "@/store/frontdesk/atoms/composer-prefill.atoms";
import { convertAttachmentsToFiles } from "@/widgets/frontdesk/providers/chatwoot/utils/attachment.utils";
import type { AttachmentSource } from "@/widgets/frontdesk/providers/chatwoot/types/attachment.types";
import { htmlToWhatsAppText } from "@/utils/html-to-whatsapp-text";
import { createLogger } from "@/services/frontdesk/utils/logger";
import { FRONTDESK_SEND_DOCUMENT_TYPES, frontDeskSendErrorKey } from "./frontdesk-document-send";

const logger = createLogger("FrontDeskDocumentSend");

/** ERP SendMessageDTO.MessageType values */
const ERP_MESSAGE_TYPE_EMAIL = 0;
const ERP_MESSAGE_TYPE_FRONTDESK = 3;

export type FrontDeskSendMode = "auto" | "manual";

export type FrontDeskUnavailableReason = "unsaved" | "noContact" | "noReachableInbox" | "checkFailed";

export interface FrontDeskSendTarget {
  channel: DocumentSendChannel;
  mode: FrontDeskSendMode;
}

/** Link an existing conversation, or create one in the inbox. */
export type FrontDeskTargetChoice =
  | { kind: "existing"; conversationId: string }
  | { kind: "new"; inboxId: number };

export type FrontDeskPrefill = Omit<ComposerPrefillRequest, "requestedAt">;

export interface FrontDeskConversationPanelState {
  conversationId: string;
  prefill: FrontDeskPrefill | null;
  prefillLoading: boolean;
}

interface DocumentContact {
  email: string;
  phone: string;
  name: string;
}

interface DocumentMailData {
  subject?: string;
  body?: string;
  attachments?: AttachmentSource[];
}

type DocumentLike = {
  id?: string;
  ticketId?: string | null;
  customerID?: string | null;
  clientName?: string | null;
  client?: { name?: string | null } | null;
  documentContacts?: Array<{
    contactMail?: string | null;
    contactPhone?: string | null;
    contactName?: string | null;
  }> | null;
};

const trimmed = (value: unknown): string => (typeof value === "string" ? value.trim() : "");

export const documentFrontDeskContact = (document: DocumentLike | null | undefined): DocumentContact => {
  const contacts = Array.isArray(document?.documentContacts) ? document!.documentContacts! : [];
  const withEmail = contacts.find((c) => trimmed(c?.contactMail));
  const withPhone = contacts.find((c) => trimmed(c?.contactPhone));
  const named = [withEmail, withPhone, ...contacts].find((c) => trimmed(c?.contactName));
  return {
    email: trimmed(withEmail?.contactMail),
    phone: trimmed(withPhone?.contactPhone),
    name: trimmed(named?.contactName) || trimmed(document?.client?.name) || trimmed(document?.clientName),
  };
};

/** Template body markup only: head, styles and scripts are not editable message content. */
export const documentBodyHtml = (html: string): string => {
  if (!html || typeof DOMParser === "undefined") return html || "";
  const parsed = new DOMParser().parseFromString(html, "text/html");
  parsed.querySelectorAll("style, script, title, meta, link").forEach((node) => node.remove());
  return parsed.body?.innerHTML?.trim() || "";
};

interface UseFrontDeskDocumentSendParams {
  documentType: DOCUMENT_TYPE;
  document: DocumentLike | null | undefined;
  /** Print-house FrontDesk feature flag; only decides whether a failed availability check is shown */
  enabled: boolean;
  /** Document-level checks before any send (e.g. a quote needs a selected item) */
  validate: () => boolean;
  /** The ERP linked the document to this conversation */
  onLinked: (conversationId: string) => void;
}

/**
 * FrontDesk send options of a GoMake document: automatic send or the conversation opened next to
 * the document with the rendered template in the composer. A document without a conversation
 * can be linked to one of the customer's active conversations or to a new one (email/WhatsApp).
 */
export const useFrontDeskDocumentSend = ({
  documentType,
  document,
  enabled,
  validate,
  onLinked,
}: UseFrontDeskDocumentSendParams) => {
  const { callApi } = useGomakeAxios();
  const { alertFault, alertSuccessSend } = useSnackBar();
  const [options, setOptions] = useState<DocumentSendOptions | null>(null);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [optionsFailed, setOptionsFailed] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [target, setTarget] = useState<FrontDeskSendTarget | null>(null);
  const [targetBusy, setTargetBusy] = useState(false);
  const [panel, setPanel] = useState<FrontDeskConversationPanelState | null>(null);
  const busyRef = useRef(false);
  /** Key of the options request in flight; responses for an older key are dropped. */
  const optionsRequestRef = useRef("");
  const panelTokenRef = useRef(0);

  const documentId = document?.id;
  const ticketId = trimmed(document?.ticketId);
  // FrontDesk inboxes decide availability (the hub answers), not the print-house feature flag.
  const supported = FRONTDESK_SEND_DOCUMENT_TYPES.includes(documentType);
  const contact = documentFrontDeskContact(document);

  /** Re-checked on every menu open: inboxes and the customer's conversations change. */
  const loadOptions = useCallback(async () => {
    if (!supported || ticketId) return;
    const key = `${documentId ?? ""}|${contact.email}|${contact.phone}`;
    if (optionsRequestRef.current === key) return;
    optionsRequestRef.current = key;
    setOptionsLoading(true);
    try {
      const result = await getFrontDeskDocumentSendService().getOptions({
        email: contact.email || undefined,
        phone: contact.phone || undefined,
      });
      if (optionsRequestRef.current === key) {
        setOptions(result);
        setOptionsFailed(false);
      }
    } catch (err) {
      logger.warn("Failed to load FrontDesk send options", err);
      if (optionsRequestRef.current === key) {
        setOptions(null);
        setOptionsFailed(true);
      }
    } finally {
      if (optionsRequestRef.current === key) {
        optionsRequestRef.current = "";
        setOptionsLoading(false);
      }
    }
  }, [supported, ticketId, documentId, contact.email, contact.phone]);

  // Options belong to one document/contact; a linked document no longer needs them.
  useEffect(() => {
    optionsRequestRef.current = "";
    setOptions(null);
    setOptionsFailed(false);
    setOptionsLoading(false);
  }, [ticketId, documentId, contact.email, contact.phone]);

  /**
   * Why an unlinked document cannot be sent through FrontDesk. Null when it can, and when FrontDesk
   * stays hidden: the print house has no inbox, or the check failed for a print house without the feature.
   */
  let unavailableReason: FrontDeskUnavailableReason | null = null;
  if (supported && !ticketId && !optionsLoading) {
    if (optionsFailed) {
      if (enabled) unavailableReason = "checkFailed";
    } else if (options?.hasInboxes) {
      if (!documentId) unavailableReason = "unsaved";
      else if (!contact.email && !contact.phone) unavailableReason = "noContact";
      else if (!options.email.available && !options.whatsapp.available) unavailableReason = "noReachableInbox";
    }
  }

  const fetchMailData = useCallback(async (): Promise<DocumentMailData | null> => {
    const res = await getDocumentMailDataApi(callApi, () => {}, {
      documentType,
      document: {
        documentId,
        messageType: ERP_MESSAGE_TYPE_EMAIL,
        sendMailIfNotVerifiedAgent: true,
        ...(contact.email && { contactEmail: contact.email }),
        ...(contact.phone && { contactPhone: contact.phone }),
      },
    });
    return res?.success && res?.data ? (res.data as DocumentMailData) : null;
  }, [callApi, documentType, documentId, contact.email, contact.phone]);

  const sendViaErp = useCallback(async (): Promise<boolean> => {
    const res = await sendDocumentToClientApi(callApi, () => {}, {
      documentType,
      document: { documentId, messageType: ERP_MESSAGE_TYPE_FRONTDESK },
    });
    if (res?.success) {
      alertSuccessSend();
      return true;
    }
    alertFault(frontDeskSendErrorKey(res));
    return false;
  }, [callApi, documentType, documentId, alertSuccessSend, alertFault]);

  const openPanel = useCallback(
    async (conversationId: string) => {
      const token = ++panelTokenRef.current;
      setPanel({ conversationId, prefill: null, prefillLoading: true });
      let prefill: FrontDeskPrefill | null = null;
      try {
        const mail = await fetchMailData();
        if (mail?.body) {
          const html = documentBodyHtml(mail.body);
          prefill = {
            html,
            text: htmlToWhatsAppText(html),
            attachments: await convertAttachmentsToFiles(mail.attachments || []),
          };
        }
      } catch {
        prefill = null;
      }
      if (token !== panelTokenRef.current) return;
      if (!prefill) alertFault("alerts.frontDeskSend.templateLoadFailed");
      setPanel({ conversationId, prefill, prefillLoading: false });
    },
    [fetchMailData, alertFault]
  );

  const closePanel = useCallback(() => {
    panelTokenRef.current += 1;
    setPanel(null);
  }, []);

  const runExclusive = useCallback(async (task: () => Promise<void>) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setIsSending(true);
    try {
      await task();
    } finally {
      busyRef.current = false;
      setIsSending(false);
    }
  }, []);

  /** Linked document: send the generated document into its conversation. */
  const sendAutomatically = useCallback(async () => {
    if (!validate()) return;
    if (!ticketId) {
      alertFault("alerts.frontDeskSend.notLinked");
      return;
    }
    await runExclusive(async () => {
      await sendViaErp();
    });
  }, [validate, ticketId, alertFault, runExclusive, sendViaErp]);

  /** Linked document: open its conversation next to the document, template in the composer. */
  const openLinkedConversation = useCallback(async () => {
    if (!validate()) return;
    if (!ticketId) {
      alertFault("alerts.frontDeskSend.notLinked");
      return;
    }
    await openPanel(ticketId);
  }, [validate, ticketId, alertFault, openPanel]);

  const linkAndContinue = useCallback(
    async (channel: DocumentSendChannel, mode: FrontDeskSendMode, choice: FrontDeskTargetChoice) => {
      let conversationId: string;
      if (choice.kind === "existing") {
        conversationId = choice.conversationId;
      } else {
        const subject =
          channel === "email" ? trimmed((await fetchMailData())?.subject) || undefined : undefined;
        const created = await getFrontDeskDocumentSendService().createConversation({
          channel,
          inboxId: choice.inboxId,
          ...(channel === "email" ? { email: contact.email } : { phone: contact.phone }),
          contactName: contact.name || undefined,
          subject,
          customerId: trimmed(document?.customerID) || undefined,
        });
        conversationId = created.conversationId;
      }

      const linked = await assignToTicketApi(callApi, () => {}, {
        documentId,
        ticketId: conversationId,
        documentType,
      });
      if (!linked?.success) {
        alertFault("alerts.frontDeskSend.linkFailed");
        return;
      }
      onLinked(conversationId);
      setTarget(null);

      if (mode === "auto") {
        await sendViaErp();
      } else {
        await openPanel(conversationId);
      }
    },
    [fetchMailData, contact.email, contact.phone, contact.name, document?.customerID, callApi, documentId, documentType, alertFault, onLinked, sendViaErp, openPanel]
  );

  /**
   * Unlinked document: ask which conversation to use when the customer already has active ones
   * or several inboxes fit; otherwise create the conversation in the only inbox right away.
   */
  const startUnlinked = useCallback(
    async (channel: DocumentSendChannel, mode: FrontDeskSendMode) => {
      if (!validate()) return;
      const channelOptions = options?.[channel];
      if (!channelOptions?.available || channelOptions.inboxes.length === 0) return;
      if (channelOptions.conversations.length > 0 || channelOptions.inboxes.length > 1) {
        setTarget({ channel, mode });
        return;
      }
      await runExclusive(async () => {
        try {
          await linkAndContinue(channel, mode, { kind: "new", inboxId: channelOptions.inboxes[0].id });
        } catch {
          alertFault(
            channel === "whatsapp"
              ? "alerts.frontDeskSend.createWhatsAppFailed"
              : "alerts.frontDeskSend.createFailed"
          );
        }
      });
    },
    [validate, options, runExclusive, linkAndContinue, alertFault]
  );

  const confirmTarget = useCallback(
    async (choice: FrontDeskTargetChoice) => {
      if (!target || busyRef.current) return;
      busyRef.current = true;
      setTargetBusy(true);
      try {
        await linkAndContinue(target.channel, target.mode, choice);
      } catch {
        alertFault(
          target.channel === "whatsapp" && choice.kind === "new"
            ? "alerts.frontDeskSend.createWhatsAppFailed"
            : "alerts.frontDeskSend.createFailed"
        );
      } finally {
        busyRef.current = false;
        setTargetBusy(false);
      }
    },
    [target, linkAndContinue, alertFault]
  );

  const closeTarget = useCallback(() => {
    if (!busyRef.current) setTarget(null);
  }, []);

  return {
    supported,
    isLinked: !!ticketId,
    options,
    optionsLoading,
    loadOptions,
    unavailableReason,
    isSending,
    sendAutomatically,
    openLinkedConversation,
    startUnlinked,
    target,
    targetOptions: target ? options?.[target.channel] ?? null : null,
    targetBusy,
    confirmTarget,
    closeTarget,
    panel,
    closePanel,
  };
};

export type FrontDeskDocumentSend = ReturnType<typeof useFrontDeskDocumentSend>;
